import { Button } from '@magicschool/spellbook/components/Button';
import { Icon, type IconProps } from '@magicschool/spellbook/components/Icon';
import { InputChip } from '@magicschool/spellbook/components/InputChip';
import { Label, type LabelProps } from '@magicschool/spellbook/components/Label';
import type { MultiSelectProps } from '@magicschool/spellbook/components/MultiSelect';
import { useMultiSelect } from '@magicschool/spellbook/components/MultiSelect/MultiSelectProvider';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { SpinnerProps } from '@magicschool/spellbook/components/Spinner';
import { Spinner } from '@magicschool/spellbook/components/Spinner';
import { Text } from '@magicschool/spellbook/components/Text';
import { useAutocompleteHighlightedLabel } from '@magicschool/spellbook/hooks/useAutocompleteHighlightedLabel';
import { useDynamicTextResolver } from '@magicschool/spellbook/hooks/useDynamicTextResolver';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { mapOrderedSlots } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import type * as PopoverPrimitive from 'radix-ui/popover';
import { forwardRef, useEffect, useMemo } from 'react';
import type { SelectOption } from '../Select';

const itemCheckboxVariants = cva('flex-0 inline-flex h-4 w-4 m-1 items-center justify-center min-w-4 min-h-4 rounded-sm border-[1.5px]', {
  variants: {
    isChecked: {
      true: [
        'bg-primary-solid-background-hover',
        'border-transparent',
        'text-primary-solid-foreground-text',
        'group-hover:bg-primary-solid-background-hover',
        'group-active:bg-primary-solid-background-active',
      ],
      false: 'bg-surface-background-0 border-checkbox group-hover:border-checkbox-hover group-hover:bg-surface-background-2',
    },
    disabled: {
      true: 'border-disabled-foreground text-disabled-foreground',
    },
  },
});

const optionLabelVariants = cva(
  [
    'flex',
    'me-4',
    'overflow-hidden',
    'text-default-text-strong',
    'group-data-[selected=true]:text-selected-default-foreground',
    'group-data-[selected=true]:group-hover:text-selected-default-foreground-hover',
    'group-data-[selected=true]:group-focus-visible:text-selected-default-foreground-hover',
    'group-data-[selected=true]:group-active:text-selected-default-foreground-active',
    'group-data-[disabled=true]:!text-disabled-foreground',
    'group-data-[disabled=true]:!cursor-not-allowed',
  ],
  {
    variants: {
      descriptionOrientation: {
        horizontal: 'flex-row gap-2 items-baseline [&_span]:truncate',
        vertical: 'flex-col grow-1 gap-0 items-start',
      },
    },
  },
);

const triggerButtonVariants = cva('w-full text-sm outline-none cursor-text disabled:cursor-not-allowed', {
  variants: {
    size: {
      md: ['h-control-h-md', 'rounded-control-md'],
      lg: ['h-control-h-lg', 'rounded-control-lg'],
      xl: ['h-control-h-xl', 'rounded-control-xl'],
    },
  },
});

const triggerWrapperVariants = cva(
  [
    'flex',
    'items-center',
    'justify-start',
    'w-full',
    'border',
    'text-sm',
    'font-normal',
    'transition-all',
    'outline-none',
    'text-transparent',
    'absolute',
    'pointer-events-none',
  ],
  {
    variants: {
      size: {
        md: ['h-control-h-md', 'rounded-control-md'],
        lg: ['h-control-h-lg', 'rounded-control-lg'],
        xl: ['h-control-h-xl', 'rounded-control-xl'],
      },
      readOnly: {
        true: 'border-surface-background-1 bg-surface-background-1',
        false: [
          'border-border-strong',
          'bg-surface-background-0',
          'group-hover:border-border-strong-hover',
          'focus-within:border-focus-ring',
          'group-hover:focus-within:border-focus-ring',
          'group-focus-within:border-focus-ring',
          'data-[state=open]:border-focus-ring',
        ],
      },
      disabled: {
        true: ['cursor-not-allowed', '!border-disabled-background', '!bg-disabled-background', 'text-disabled-foreground'],
      },
      invalid: {
        true: [
          /**
           * @TODO [SPE-105] Centralize "invalid"/state color tokens to single utility
           * https://linear.app/magicschool/issue/SPE-105/centralize-invalid-state-color-tokens
           */
          'border-error-stroke',
          'group-hover:border-error-stroke',
          'group-focus-within:border-error-stroke',
          'group-hover:focus-within:border-error-stroke',
          'data-[state=open]:border-error-stroke',
          'has-[[data-state=open]]:border-error-stroke',
          'group-focus-within:border-error-stroke',
        ],
      },
    },
    defaultVariants: {
      readOnly: false,
    },
    compoundVariants: [
      {
        invalid: true,
        disabled: true,
        className: 'border-disabled-background hover:border-disabled-background focus-within:border-disabled-background',
      },
    ],
  },
);

const triggerLeadingAddOnVariants = cva(
  [
    'items-center',
    'justify-center',
    'inline-flex',
    'shrink-0',
    'h-full',
    'ltr:border-r-1',
    'ltr:border-r-border-default',
    'rtl:border-l-1',
    'rtl:border-l-border-default',
  ],
  {
    variants: {
      size: {
        md: ['px-2'],
        lg: ['px-3'],
        xl: ['px-3'],
      },
      disabled: {
        true: 'ltr:border-r-disabled-background rtl:border-l-disabled-background',
      },
    },
  },
);

const triggerTrailingAddOnVariants = cva(
  [
    'items-center',
    'justify-center',
    'inline-flex',
    'shrink-0',
    'h-full',
    'ltr:border-l-1',
    'ltr:border-l-border-default',
    'rtl:border-r-1',
    'rtl:border-r-border-default',
  ],
  {
    variants: {
      size: {
        md: ['px-2'],
        lg: ['px-3'],
        xl: ['px-3'],
      },
      disabled: {
        true: 'ltr:border-l-disabled-background rtl:border-r-disabled-background',
      },
    },
  },
);

const triggerInputVariants = cva(
  [
    'cursor-text',
    'text-default-text-strong',
    'focus-visible:outline-0',
    'w-full',
    'h-full',
    'bg-transparent',
    'placeholder:text-placeholder',
    'transition-colors',
    'group-hover/text-input:placeholder:text-placeholder-hover',
    'border-0',
    'relative',
  ],
  {
    variants: {
      size: {
        md: 'h-control-h-md ps-2 pe-2',
        lg: 'h-control-h-lg ps-2 pe-3',
        xl: 'h-control-h-xl ps-2 pe-3',
      },
      disabled: {
        true: 'placeholder:!text-disabled-foreground !text-disabled-foreground',
      },
      readOnly: {
        true: 'cursor-text placeholder:!text-disabled-foreground',
      },
    },
  },
);

const triggerAdornmentVariants = cva('flex-0', {
  variants: {
    size: {
      md: 'ltr:pl-2 rtl:pr-2 ltr:-mr-1 rtl:-ml-1',
      lg: 'ltr:pl-3 rtl:pr-3 ltr:-mr-1 rtl:-ml-1',
      xl: 'ltr:pl-3 rtl:pr-3 ltr:-mr-1 rtl:-ml-1',
    },
    disabled: {
      true: '!stroke-disabled-foreground',
    },
  },
});

const triggerIconVariants = cva(
  [
    'flex-0',
    'absolute',
    'top-1/2',
    'transform',
    '-translate-y-1/2',
    'pointer-events-none',
    'text-default-icon',
    'group-focus-within:text-primary-icon',
  ],
  {
    variants: {
      size: {
        md: 'ltr:right-2 rtl:left-2',
        lg: 'ltr:right-3 rtl:left-3',
        xl: 'ltr:right-3 rtl:left-3',
      },
      disabled: {
        true: '!text-disabled-foreground group-focus-within:!text-disabled-foreground',
      },
      invalid: {
        true: 'text-error-icon group-focus-within:!text-error-icon',
      },
    },
    compoundVariants: [
      {
        invalid: true,
        disabled: true,
        className: 'text-disabled-foreground',
      },
    ],
  },
);

export type MultiSelectTriggerProps = React.ComponentProps<typeof PopoverPrimitive.Trigger> &
  Pick<MultiSelectProps, 'placeholder' | 'name' | 'readOnly' | 'labelId' | 'size' | 'id' | 'invalid' | 'disabled' | 'data-testid'> & {
    comboboxId?: string;
    contentId?: string;
    /** Sets `aria-required` on the combobox input; the native `required` cannot apply to the hidden value inputs */
    required?: boolean;
  };

export type MultiSelectContentProps = React.ComponentPropsWithoutRef<'div'> &
  Pick<MultiSelectProps, 'descriptionOrientation' | 'disabled' | 'isLoading' | 'size'>;

export type MultiSelectOptionProps = React.ComponentPropsWithoutRef<'div'> & SelectOption & Pick<MultiSelectProps, 'size'>;

export type MultiSelectGroupProps = React.ComponentPropsWithoutRef<'div'> &
  Pick<MultiSelectProps, 'size'> & {
    label: string;
  };

export const MultiSelectTriggerSlot = Slot<MultiSelectTriggerProps>({
  displayName: 'MultiSelect.Trigger',
  isRenderedByDefault: true,
  renderDefault: (props) => {
    const MultiSelectTrigger: React.FC<MultiSelectTriggerProps> = useMemo(
      () =>
        forwardRef(
          (
            {
              'data-testid': dataTestId,
              disabled,
              id,
              labelId,
              contentId,
              comboboxId,
              invalid,
              placeholder,
              readOnly,
              size,
              children,
              required,
              'aria-describedby': ariaDescribedBy,
            },
            ref,
          ) => {
            const {
              inputRef,
              handleInputOnBlur,
              handleInputOnChange,
              handleInputOnClick,
              handleInputOnFocus,
              handleInputOnKeyDown,
              handleTriggerOnBlur,
              handleTriggerOnClick,
              handleTriggerOnFocus,
              open,
              searchString,
            } = useMultiSelect();

            const { slots } = useSlots(children, {
              leadingAddOn: { Component: MultiSelectLeadingAddOnSlot },
              leadingAdornment: { Component: MultiSelectLeadingAdornmentSlot, props: { ...(disabled && { color: 'disabled' }) } },
              trailingAddOn: { Component: MultiSelectTrailingAddOnSlot },
              trailingAdornment: { Component: MultiSelectTrailingAdornmentSlot, props: { open, readOnly, disabled } },
            });

            return (
              <div data-spellbook-name="multi-select-trigger" className="flex items-center relative w-full sm:min-w-form-field-min group">
                <button
                  type="button"
                  className={triggerButtonVariants({ size })}
                  data-testid={dataTestId}
                  id={id}
                  onClick={handleTriggerOnClick}
                  onDoubleClick={handleTriggerOnClick}
                  onFocus={handleTriggerOnFocus}
                  onBlur={handleTriggerOnBlur}
                  ref={ref}
                  disabled={disabled}
                  tabIndex={-1}
                >
                  {placeholder}
                </button>
                <span className={cn(triggerWrapperVariants({ size, readOnly, disabled, invalid }))} data-state={open ? 'open' : 'closed'}>
                  {slots.leadingAddOn && <span className={triggerLeadingAddOnVariants({ size, disabled })}>{slots.leadingAddOn}</span>}
                  <span className="w-full h-full relative flex items-center">
                    {slots.leadingAdornment && (
                      <span className={triggerAdornmentVariants({ size, disabled })}>{slots.leadingAdornment}</span>
                    )}
                    <input
                      aria-invalid={invalid ? 'true' : undefined}
                      aria-label={placeholder}
                      aria-haspopup="listbox"
                      aria-autocomplete="list"
                      aria-controls={contentId}
                      autoComplete="off"
                      role="combobox"
                      id={comboboxId}
                      aria-expanded={open ? 'true' : 'false'}
                      aria-required={required || undefined}
                      aria-describedby={ariaDescribedBy}
                      {...(labelId && { 'aria-labelledby': labelId })}
                      className={triggerInputVariants({ size, disabled, readOnly })}
                      type="text"
                      value={searchString}
                      onChange={handleInputOnChange}
                      onKeyDown={handleInputOnKeyDown}
                      onClick={handleInputOnClick}
                      onFocus={handleInputOnFocus}
                      onBlur={handleInputOnBlur}
                      placeholder={placeholder}
                      readOnly={readOnly}
                      disabled={disabled}
                      ref={inputRef}
                    />
                    {slots.trailingAdornment && (
                      <span className={triggerIconVariants({ size, invalid, disabled })}>{slots.trailingAdornment}</span>
                    )}
                  </span>
                  {slots.trailingAddOn && <span className={triggerTrailingAddOnVariants({ size, disabled })}>{slots.trailingAddOn}</span>}
                </span>
              </div>
            );
          },
        ),
      [],
    );

    return <MultiSelectTrigger {...props} />;
  },
});

export const MultiSelectOptionSlot = Slot<MultiSelectOptionProps>({
  displayName: 'MultiSelect.Option',
  renderDefault: ({ value, className, label: providedLabel, description, disabled, children, size, ...props }) => {
    const { disableDefaultFiltering, options, disableTextHighlighting, searchString, valueArray } = useMultiSelect();
    const opts = Array.from(options.values());
    const index = opts.findIndex((option) => option.value === value);
    const label = providedLabel || (typeof children === 'string' ? children : value);
    const highlightedLabel = useAutocompleteHighlightedLabel(label, searchString, { disableTextHighlighting });

    const isFiltered = useMemo(() => {
      return (
        disableDefaultFiltering !== true && searchString && !label.toString().toLowerCase().includes(searchString.toLowerCase().trim())
      );
    }, [searchString, label, disableDefaultFiltering]);

    const {
      registerOption,
      unregisterOption,
      descriptionOrientation,
      optionRefs,
      handleOptionOnClick,
      handleOptionOnKeyDown,
      handleOptionOnMouseEnter,
    } = useMultiSelect();

    useEffect(() => {
      const option = {
        value,
        label,
        description,
        disabled,
        highlightedLabel,
      };

      if (!isFiltered) {
        registerOption(option);
      }

      return () => {
        if (!isFiltered) {
          unregisterOption(value);
        }
      };
    }, [value, label, description, disabled, highlightedLabel, children, registerOption, searchString, unregisterOption, isFiltered]);

    const displayLabel = label || (typeof children === 'string' ? children : value);
    const isSelected = valueArray.includes(value);

    if (isFiltered) {
      return null;
    }

    return (
      <div
        data-spellbook-name="multi-select-option"
        className={cn([
          'flex',
          'cursor-pointer',
          'items-center',
          'justify-between',
          'outline-none',
          'py-2',
          'gap-2',
          'group',
          'data-[selected=true]:bg-selected-default-background',
          'data-[selected=true]:hover:bg-selected-default-background-hover',
          'data-[selected=true]:active:bg-selected-default-background-active',
          'data-[selected=true]:focus-visible:bg-selected-default-background-hover',
          '[&:not(:focus-visible):hover]:bg-surface-overlay-1',
          'focus-visible:bg-surface-overlay-1',
          'data-[disabled=true]:!cursor-not-allowed',
          'data-[disabled=true]:!bg-transparent',
          size === 'md' ? 'px-2' : 'px-3',
          className,
        ])}
        data-disabled={disabled}
        aria-disabled={disabled}
        key={value}
        onClick={(e) => handleOptionOnClick(e, value)}
        onKeyDown={(e) => handleOptionOnKeyDown(e, index)}
        onMouseEnter={(e) => handleOptionOnMouseEnter(e, index)}
        ref={(node) => {
          if (optionRefs[index]) {
            optionRefs[index].current = node;
          }
        }}
        tabIndex={disabled ? -1 : 0}
        // biome-ignore lint/a11y/useSemanticElements: composite multiselect popover
        role="option"
        data-selected={isSelected}
        aria-selected={isSelected}
        {...props}
      >
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2 overflow-hidden">
            <span className={itemCheckboxVariants({ isChecked: isSelected, disabled })}>
              {isSelected && <Icon name="check" color="inherit" size="xs" />}
            </span>
            <span className={optionLabelVariants({ descriptionOrientation })}>
              <Text variant="body-2" element="span" color="inherit" className="max-w-full shrink-0" aria-label={displayLabel}>
                {(!disabled && highlightedLabel) || displayLabel}
              </Text>
              {description && (
                <Text variant="caption-1" color="default-soft">
                  {description}
                </Text>
              )}
            </span>
          </span>
        </span>
      </div>
    );
  },
});

export const MultiSelectContentSlot = Slot<MultiSelectContentProps>({
  displayName: 'MultiSelect.Content',
  isRenderedByDefault: true,
  renderDefault: ({ children, className, descriptionOrientation, size, ...props }) => {
    const { searchString, isLoading, options, disableDefaultNoResults, text } = useMultiSelect();

    const { slots, rest } = useSlots(children, {
      spinner: { Component: MultiSelectSpinnerSlot },
    });

    const { orderedSlots } = useOrderedSlots(rest, {
      option: { Component: MultiSelectOptionSlot, props: { size } },
      group: { Component: MultiSelectGroupSlot, props: { size } },
      emptyState: { Component: MultiSelectEmptySlot, props: { size } },
    });

    return (
      <div data-spellbook-name="multi-select-content" className={cn('sm:min-w-form-field-min w-full max-h-64', className)} {...props}>
        {isLoading ? (
          <div className="flex px-4 items-center justify-center py-2">{slots.spinner}</div>
        ) : (
          <>
            {mapOrderedSlots(orderedSlots)}
            {searchString.length > 0 && options.size === 0 && disableDefaultNoResults !== true && (
              <div className="flex px-4 py-2">
                <Text variant="caption-1" color="default-soft">
                  {text?.noResults}
                </Text>
              </div>
            )}
          </>
        )}
      </div>
    );
  },
});

export const MultiSelectSpinnerSlot = Slot<SpinnerProps>({
  displayName: 'MultiSelect.Spinner',
  isRenderedByDefault: true,
  renderDefault: ({ color = 'primary', ...props }) => {
    return <Spinner data-spellbook-name="multi-select-spinner" color={color} {...props} />;
  },
});

export const MultiSelectGroupSlot = Slot<MultiSelectGroupProps>({
  displayName: 'MultiSelect.Group',
  renderDefault: ({ className, children, label, size, id: providedId, ...props }) => {
    const id = useId(providedId);
    const { options, searchString } = useMultiSelect();
    const { orderedSlots } = useOrderedSlots(children, {
      option: { Component: MultiSelectOptionSlot },
    });

    const { visibleSlots, shouldRender, shouldSimplyRegister } = useMemo(() => {
      let filteredSlots = orderedSlots;

      if (searchString) {
        filteredSlots = orderedSlots.filter((slot) => {
          const props = slot.component.props as { value?: string; label?: string };
          const label = props.label || props.value || '';
          return label.toLowerCase().includes(searchString.toLowerCase().trim());
        });
      }

      const hasVisibleSlots = filteredSlots.length > 0;

      const slotValues = filteredSlots.map((slot) => (slot.component.props as { value?: string }).value) || [];
      const shouldSimplyRegister =
        slotValues.length === 0 || slotValues.every((value) => !Array.from(options.values()).some((option) => option.value === value));

      return {
        visibleSlots: filteredSlots,
        shouldRender: hasVisibleSlots,
        shouldSimplyRegister,
      };
    }, [orderedSlots, searchString, options]);

    if (!shouldRender) {
      return null;
    }

    if (shouldSimplyRegister) {
      return visibleSlots.map((slot) => slot.component);
    }

    return (
      <div
        data-spellbook-name="multi-select-group"
        className={cn('border-t border-t-border-default pt-2 first-of-type:pt-1 first-of-type:border-0', className)}
        // biome-ignore lint/a11y/useSemanticElements: composite multiselect popover
        role="group"
        aria-labelledby={id}
        {...props}
      >
        <Text
          variant="caption-1"
          color="default-soft"
          className={cn('py-2 flex', size === 'md' ? 'px-2' : 'px-3')}
          id={id}
          role="presentation"
        >
          {label}
        </Text>
        {visibleSlots.map((slot) => slot.component)}
      </div>
    );
  },
});

export const MultiSelectEmptySlot = Slot<React.ComponentPropsWithoutRef<'div'> & { size: MultiSelectProps['size'] }>({
  displayName: 'MultiSelect.Empty',
  renderDefault: ({ className, children, size, ...props }) => {
    return (
      <div data-spellbook-name="multi-select-empty" className={cn('flex py-2', size === 'md' ? 'px-2' : 'px-3', className)} {...props}>
        <Text variant="caption-1" color="default-soft">
          {children}
        </Text>
      </div>
    );
  },
});

export const MultiSelectLeadingAddOnSlot = Slot<LabelProps>({
  displayName: 'MultiSelect.LeadingAddOn',
  renderDefault: ({ children, className, ...props }) => {
    return (
      <Label data-spellbook-name="multi-select-leading-add-on" className={cn('font-normal text-default-text-soft', className)} {...props}>
        {children}
      </Label>
    );
  },
});

export const MultiSelectLeadingAdornmentSlot = Slot<IconProps>({
  displayName: 'MultiSelect.LeadingAdornment',
  renderDefault: ({ name = 'square-dashed', className, size: _, color = 'default', ...props }) => {
    return (
      <Icon
        data-spellbook-name="multi-select-leading-adornment"
        name={name}
        size="sm"
        color={color}
        className={cn('m-1', className)}
        {...props}
      />
    );
  },
});

export const MultiSelectTrailingAddOnSlot = Slot<LabelProps>({
  displayName: 'MultiSelect.TrailingAddOn',
  renderDefault: ({ children, className, ...props }) => {
    return (
      <Label data-spellbook-name="multi-select-trailing-add-on" className={cn('font-normal text-default-text-soft', className)} {...props}>
        {children}
      </Label>
    );
  },
});

export const MultiSelectTrailingAdornmentSlot = Slot<IconProps & { open?: boolean; readOnly?: boolean; disabled?: boolean }>({
  displayName: 'MultiSelect.TrailingAdornment',
  isRenderedByDefault: true,
  renderDefault: ({ name = 'chevron-down', color = 'inherit', className, open, readOnly, disabled, ...props }) =>
    !readOnly ? (
      <Icon
        data-spellbook-name="multi-select-trailing-adornment"
        name={name}
        size="sm"
        color={color}
        className={cn(open ? 'transform rotate-180' : '', 'm-1', className)}
        {...props}
      />
    ) : null,
});

export const MultiSelectChipsSlot = Slot<{
  className?: string;
  filterChipVisibleLimit?: number;
  id?: string;
  disabled?: boolean;
  readOnly?: boolean;
}>({
  displayName: 'MultiSelect.Chips',
  preventOverride: true,
  renderDefault: ({ className, disabled, readOnly, filterChipVisibleLimit, id: providedId, ...props }) => {
    const { valueArray, handleRemoveValue, handleRemoveAllValues, getOptionByValue, text } = useMultiSelect();
    const id = useId(providedId);
    const { dismissibleLabel, andMore, clearAll = 'Clear All' } = text ?? {};

    const resolveAndMore = useDynamicTextResolver(andMore, (count: number) => `+${count} more`);
    const resolveDismissibleLabel = useDynamicTextResolver(dismissibleLabel, () => 'Remove item');

    if (valueArray.length === 0) {
      return null;
    }

    return (
      <div data-spellbook-name="multi-select-chips" className={cn('flex w-full justify-between gap-2 mt-2', className)} {...props}>
        <div className="flex flex-wrap justify-start items-center gap-2">
          {(filterChipVisibleLimit ? valueArray.slice(0, filterChipVisibleLimit) : valueArray).map((value) => {
            const option = getOptionByValue(value);
            const label = option?.label || value;

            return (
              <InputChip
                key={value}
                id={`${id}-tag-${value.replace(/\s+/g, '-')}`.toLowerCase()}
                onDismiss={() => handleRemoveValue(value)}
                disabled={disabled}
                readOnly={readOnly}
                text={{ dismissibleLabel: resolveDismissibleLabel(label) }}
              >
                {label}
              </InputChip>
            );
          })}
          {filterChipVisibleLimit && valueArray.length > filterChipVisibleLimit && (
            <Text variant="caption-1" color="default-soft">
              {resolveAndMore(valueArray.length - filterChipVisibleLimit)}
            </Text>
          )}
        </div>
        {valueArray.length > 1 && (
          <Button variant="ghost" size="sm" onClick={handleRemoveAllValues}>
            {clearAll}
          </Button>
        )}
      </div>
    );
  },
});
